/** tm/legal/legalUi.tsx — 법령·판례·뉴스 공용 소형 컴포넌트 (배지·토스트·전역 스타일·반응형 폭) */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { C, T, R } from '../components/ui';
import { groupColor, billStatusColor, MINE_FG, MUTED } from './legalTheme';
export { useWindowWidth } from '../../shared';

export const MOBILE_BP = 860;

export function SourceBadge({ group, label }: { group: string | null; label: string }) {
  const c = groupColor(group);
  return <span style={{ fontSize: T.sm, fontWeight: 700, padding: '2px 6px', borderRadius: 4, whiteSpace: 'nowrap', background: c.bg, color: c.fg }}>{label}</span>;
}

export function BillStatusBadge({ status }: { status: string | null }) {
  const c = billStatusColor(status);
  return <span style={{ fontSize: T.sm, fontWeight: 700, padding: '2px 6px', borderRadius: 10, whiteSpace: 'nowrap', background: c.bg, color: c.fg }}>{status || '계류'}</span>;
}

/** 항목 제목 — 내가 바꾼 제목이 있으면 보라색 내 제목 + 회색 보통 굵기 «(원래 제목)».
 *  목록·줄 안에서는 인라인 — 줄임(2줄 clamp·한 줄 ellipsis)은 감싼 요소가 둘을 한꺼번에 자른다. 전체는 title 툴팁 */
export function ItemTitle({ title, custom }: { title: string; custom?: string | null }) {
  if (!custom) return <>{title}</>;
  return <span title={itemTitleText(title, custom)}><span style={{ color: MINE_FG }}>{custom}</span><span style={{ color: MUTED, fontWeight: 400 }}> ({title})</span></span>;
}
/** 상세 제목의 내 제목 부분 (보라) */
export function MineTitle({ text }: { text: string }) {
  return <span style={{ color: MINE_FG }}>{text}</span>;
}
/** 상세 제목 아래 «(원래 제목)» — 한 줄 말줄임, 전체는 툴팁 (내 제목은 길어도 그대로 다 보인다) */
export function OriginalTitleLine({ title }: { title: string }) {
  return (
    <span title={title} style={{ display: 'block', marginTop: 2, fontSize: T.md, fontWeight: 400, color: MUTED, lineHeight: 1.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
      ({title})
    </span>
  );
}
/** title 속성·스크린리더용 한 줄 */
export const itemTitleText = (title: string, custom?: string | null) => (custom ? `${custom} (${title})` : title);

export function TopicTag({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: T.sm, padding: '2px 7px', borderRadius: 4, background: C.bg, color: C.sub, whiteSpace: 'nowrap' }}>{children}</span>;
}

/** 인라인 스타일로 표현하기 어려운 부분(포커스 링·hover·스피너·줄임)만 */
export function LegalGlobalStyle() {
  return <style>{`
.lw-f:focus-visible{outline:2px solid ${C.accent};outline-offset:1px}
.lw-card{transition:border-color .12s,box-shadow .12s}
.lw-card:hover{border-color:${C.borderDk}!important}
.lw-row:hover{background:${C.bgAlt}!important}
.lw-fi:hover{background:${C.bg}}
@keyframes lw-spin{to{transform:rotate(360deg)}}
.lw-spin{display:inline-block;width:10px;height:10px;border:2px solid currentColor;border-right-color:transparent;border-radius:50%;animation:lw-spin .8s linear infinite}
.lw-clamp{display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.lw-skel{background:linear-gradient(90deg,${C.bg} 0%,${C.bgAlt} 50%,${C.bg} 100%);background-size:200% 100%;animation:lw-sh 1.2s ease-in-out infinite}
@keyframes lw-sh{from{background-position:200% 0}to{background-position:-200% 0}}
.lw-noscroll{scrollbar-width:none}.lw-noscroll::-webkit-scrollbar{display:none}
.lw-fade{position:relative}.lw-fade::after{content:'›';position:absolute;right:0;top:0;bottom:0;width:30px;display:flex;align-items:center;justify-content:flex-end;padding-right:2px;color:${C.sub};font-weight:700;pointer-events:none;background:linear-gradient(90deg,rgba(243,244,246,0),${C.bg} 60%)}
.lw-fade.lw-end::after{display:none}
.lw-search:focus{border-color:${C.accent}!important;box-shadow:0 0 0 3px ${C.accentLt}}
.lw-search:focus-visible{outline:2px solid ${C.accent};outline-offset:1px}
@media (max-width:${MOBILE_BP - 1}px){.lw-root input[type=checkbox]{width:24px;height:24px;flex-shrink:0}.lw-root input:not([type=checkbox]):not([type=radio]),.lw-root select{min-height:40px;box-sizing:border-box}.lw-root label:has(input[type=checkbox]){min-height:44px;align-items:center}button.lw-f,a.lw-f,select.lw-f{min-height:40px}button.lw-f{min-width:40px}.lw-m40{min-height:40px!important;min-width:40px}}
@media (prefers-reduced-motion:reduce){.lw-card{transition:none}.lw-spin{animation-duration:2s}.lw-skel{animation:none}}
`}</style>;
}


type ToastKind = 'ok' | 'error';
export interface ToastAction { label: string; run: () => void }
export type ShowToast = (msg: string, kind?: ToastKind, action?: ToastAction) => void;
/** 하단 토스트 — [노드, show(msg, kind, action?)]. action(「되돌리기」 등)이 있으면 5초 동안 버튼과 함께 */
export function useToast(): [ReactNode, ShowToast] {
  const [t, setT] = useState<{ msg: string; kind: ToastKind; action?: ToastAction } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback<ShowToast>((msg, kind = 'ok', action) => {
    setT({ msg, kind, action });
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setT(null), action ? 5000 : 2600);
  }, []);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const node = t ? (
    <div role="status" style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', padding: t.action ? '6px 8px 6px 18px' : '10px 18px', borderRadius: R.md,
      background: t.kind === 'error' ? C.red : C.text, color: C.white, fontSize: T.md, fontWeight: 600, zIndex: 3000,
      boxShadow: '0 4px 12px rgba(0,0,0,.2)', maxWidth: 'calc(100vw - 32px)', display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span onClick={() => setT(null)} style={{ cursor: 'pointer' }}>{t.msg}</span>
      {t.action && <button type="button" className="lw-f" onClick={() => { const a = t.action; setT(null); a?.run(); }} style={{
        border: 0, background: 'rgba(255,255,255,.16)', color: '#93C5FD', fontWeight: 700, fontSize: T.md, borderRadius: R.sm, padding: '6px 12px', cursor: 'pointer', fontFamily: 'inherit', minHeight: 36,
      }}>{t.action.label}</button>}
    </div>
  ) : null;
  return [node, show];
}

export function Spinner() { return <span className="lw-spin" aria-hidden="true" />; }

/** 배너 (info · error · warn) */
export function Banner({ kind, children }: { kind: 'info' | 'error' | 'warn'; children: ReactNode }) {
  const s = kind === 'error' ? { bg: C.redLt, bd: '#FECACA', fg: '#991B1B' }
    : kind === 'warn' ? { bg: '#FFFBEB', bd: '#FDE68A', fg: '#92400E' } : { bg: C.accentLt, bd: '#BFDBFE', fg: '#1E40AF' };
  return (
    <div role={kind === 'error' ? 'alert' : 'status'} style={{ background: s.bg, borderBottom: `1px solid ${s.bd}`, color: s.fg, padding: '8px 20px', fontSize: T.sm, lineHeight: 1.6 }}>
      {children}
    </div>
  );
}
